import { EWorldMapSetType } from '__generated__/globalTypes';
import { useAnalytics } from 'libs/utils/site/analytics';
import { CustomActionButton } from 'pages/custom/shared/CustomHeader/CustomActionButton';
import { useContext } from 'react';
import { CtxModal } from 'session/ctxModal';
import { CtxView } from 'session/ctxView';
import { Localization } from 'static/localization/localization';
import styled from 'styled-components/macro';

interface Props {
    programmeTitle?: string;
    programeId?: string;
    onProgrammeClicked?: () => void;
    listTitle?: 'companies' | 'places';
    hideHighlight?: boolean;
    hideProgramme?: boolean;
    className?: string;
}

export const CompanyButtonsWithProgramme = (props: Props): JSX.Element => {
    const ctxView = useContext(CtxView);
    const ctxModal = useContext(CtxModal);
    const analytics = useAnalytics();

    const allCategories = ctxView.map.worldMapSets.filter(
        (e) => e.type === EWorldMapSetType.CATEGORY
    );
    const visibleCategories = allCategories.filter((category) =>
        ctxView.visibleMapSetIds?.includes(category.worldMapSetId)
    );

    let companiesText = props.listTitle === 'places' ? Localization.PLACES : Localization.COMPANIES;
    if (ctxModal.storyList?.visibleStoryIds?.length && ctxView.conectedDetails?.length) {
        const totalCompaniesCount =
            ctxView.conectedDetails?.length -
            (ctxView.customPageConfig.storyIdsExcludedInList?.length ?? 0);

        companiesText += ` ${ctxModal.storyList?.visibleStoryIds?.length}/${totalCompaniesCount}`;
    }

    let filtersText = Localization.FILTERS;
    if (visibleCategories?.length && allCategories?.length) {
        filtersText += ` ${visibleCategories?.length}/${allCategories?.length}`;
    }

    function openProgramme() {
        if (props.onProgrammeClicked) {
            props.onProgrammeClicked();
        } else if (props.programeId) {
            const story = ctxView.conectedDetails?.find((s) => s.storyId === props.programeId);
            if (story) {
                ctxModal.setDetailModal({
                    id: props.programeId,
                    detail: story,
                    markerId: null
                });
            }
        }
    }

    return (
        <S_CompanyButtonsWithProgramme className={props.className}>
            <S_Button
                iconSet="far"
                iconName={props.listTitle === 'places' ? 'location-dot' : 'building'}
                text={companiesText}
                onClick={() => {
                    analytics.track('custom_button.clicked', {
                        action: 'sidebar_list.opened'
                    });
                    ctxModal.setShowListOfStories(true);
                }}
            />

            {Boolean(allCategories.length) && (
                <S_Button
                    iconSet="far"
                    iconName="filter"
                    text={filtersText}
                    onClick={() => {
                        analytics.track('custom_button.clicked', {
                            action: 'sidebar_filters.opened'
                        });
                        ctxModal.setShowFilters(true);
                    }}
                />
            )}

            {!props.hideProgramme && (
                <S_Button
                    iconSet="far"
                    iconName={'book'}
                    text={props.programmeTitle ?? Localization.PROGRAMME}
                    onClick={(e) => {
                        analytics.track('custom_button.clicked', {
                            action: 'sidebar_programme.opened'
                        });
                        openProgramme();
                        e.stopPropagation();
                    }}
                />
            )}

            {!props.hideHighlight && (
                <S_Button
                    iconSet="far"
                    iconName="highlighter"
                    text={
                        ctxView.forceMarkersHighlight
                            ? Localization.UNHIGHLIGHT
                            : Localization.HIGHLIGHT
                    }
                    onClick={(e: React.MouseEvent) => {
                        analytics.track('custom_button.clicked', {
                            action: 'highlight_markers'
                        });
                        ctxView.setForceMarkersHighlight(!ctxView.forceMarkersHighlight);
                        e.stopPropagation();
                    }}
                />
            )}
        </S_CompanyButtonsWithProgramme>
    );
};

const S_CompanyButtonsWithProgramme = styled.div`
    display: flex;
    width: 100%;
    height: 100%;
    @media only screen and (min-width: 1259px) {
        width: unset;
    }
`;

const S_Button = styled(CustomActionButton)`
    width: 100%;
    @media only screen and (min-width: 1259px) {
        min-width: 150px;
        width: unset;
    }
`;
